import React, { ReactNode, useRef } from 'react';
import cn from 'classnames';
import { useAuthContext } from '@repo/hooks';
import {
  Section,
  SettingsSectionBox,
  SettingsSectionLabel,
  SettingsInlineButton,
} from '@views/Settings/components';
import styles from './SwitchPlan.module.scss';
import { useWorkspaceContext } from '@repo/hooks';
import { ProductName } from '@repo/utils/types';
import openURL from '@repo/utils/helpers/open-url';
import { openChurnkeyModal } from '../CancelPlan';
import { useDispatch } from 'react-redux';
import upgradeAction from '@support/upgrade-action';

interface SwitchPlansBoxProps {
  plan: ProductName;
  title: string;
  description: ReactNode;
  switchToPlan: () => void;
  skip?: boolean;
}

const SwitchPlansBox = ({ plan, title, description, switchToPlan }: SwitchPlansBoxProps) => {
  return (
    <SettingsSectionBox className={cn(styles.switchPlansBox, styles[plan.split(' ').join('-')])}>
      {title}
      <SettingsInlineButton onClick={switchToPlan} className={styles.switchPlansButton}>
        Switch
      </SettingsInlineButton>
      <p>
        {description}
        {!window.desktopIntegrations?.appInfo.isMAS && (
          <>
            {' '}
            <a href="https://clay.earth/pricing" target="_blank">
              Learn more
            </a>
          </>
        )}
        .
      </p>
    </SettingsSectionBox>
  );
};

export default function SwitchPlan() {
  const dispatch = useDispatch();
  const workspaceCtx = useWorkspaceContext();
  const { user } = useAuthContext();
  const userIsAdmin = useRef(workspaceCtx.activeWorkspace && !workspaceCtx.userIsAdmin);

  const planBoxes: SwitchPlansBoxProps[] = [
    {
      plan: 'Clay Teams',
      title: 'Teams',
      description: 'Collaborative networking for your team.',
      switchToPlan: () => openURL(user?.upgrade_url),
      skip: window.desktopIntegrations?.appInfo.isMAS,
    },
    {
      plan: 'Clay Pro',
      title: 'Pro',
      description: 'Unlimited contacts, data refreshes, and priority support.',
      switchToPlan: upgradeAction(user, dispatch),
    },
    {
      plan: 'Clay Personal',
      title: 'Personal',
      description: 'Basic organization up to 1000 relationships.',
      switchToPlan: () => openChurnkeyModal({ user, switchingToPersonalPlan: true }),
      skip: !user?.churnkey,
    },
  ];

  if (!user || !userIsAdmin) {
    return null;
  }

  if (user?.billing_provider !== 'Stripe' || !user?.stripe_customer_id) {
    return null;
  }

  return (
    <Section>
      <SettingsSectionLabel>Switch Plan</SettingsSectionLabel>
      <div className={styles.boxes}>
        {planBoxes
          .filter(({ plan, skip }) => plan !== user.primary_product.name && !skip)
          .map((props) => (
            <SwitchPlansBox key={props.plan} {...props} />
          ))}
      </div>
    </Section>
  );
}
